import { Link } from 'react-router-dom';
import { StaggerList, ListItem } from '../ui/Motion.js';
import { BookOpen, GraduationCap, FileText } from 'lucide-react';
import type { ProgressResponse } from '@xdface-vault/shared';
import { SkeletonCard } from '../ui/SkeletonCard.js';

interface ContinueReadingProps {
  items: ProgressResponse[];
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
}

export function ContinueReading({ items, isLoading, isError, onRetry }: ContinueReadingProps) {
  return (
    <section aria-labelledby="continue-heading">
      <div className="mb-4 flex items-center justify-between gap-3">
        <h2
          id="continue-heading"
          className="text-sm font-semibold text-[var(--color-text)] uppercase tracking-wider"
        >
          En progreso
        </h2>
        {items.length > 0 && (
          <Link to="/progress" className="text-xs text-xd-action hover:underline">
            Ver todo
          </Link>
        )}
      </div>
      {isLoading ? (
        <div role="status">
          <span className="sr-only">Cargando progreso</span>
          <SkeletonCard />
        </div>
      ) : isError ? (
        <div
          role="alert"
          className="rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4"
        >
          <p className="text-sm text-[var(--color-text)]">No se pudo cargar tu progreso.</p>
          <button
            type="button"
            onClick={onRetry}
            className="mt-2 text-sm font-medium text-xd-action hover:underline"
          >
            Reintentar
          </button>
        </div>
      ) : items.length === 0 ? (
        <div className="rounded-xl border border-dashed border-[var(--color-border)] bg-[var(--color-surface)] p-8 text-center">
          <BookOpen className="mx-auto w-12 h-12 text-text-subtle" />
          <p className="mt-3 font-medium text-[var(--color-text)]">Sin lecturas en progreso</p>
          <p className="mt-1 text-sm text-[var(--color-text-muted)]">
            Registrá tu progreso de lectura
          </p>
          <Link
            to="/progress"
            className="mt-4 inline-flex items-center gap-2 rounded-lg border border-xd-action px-4 py-2 text-sm font-medium text-xd-action hover:bg-xd-action/10 transition-colors"
          >
            Registrar lectura
          </Link>
        </div>
      ) : (
        <StaggerList className="space-y-3">
          {items.map((item) => {
            const icon =
              item.type === 'book' ? (
                <BookOpen className="w-4 h-4 text-xd-action" />
              ) : item.type === 'course' ? (
                <GraduationCap className="w-4 h-4 text-xd-action" />
              ) : (
                <FileText className="w-4 h-4 text-xd-action" />
              );
            return (
              <ListItem key={`${item.type}-${item.slug}`}>
                <Link
                  to="/progress"
                  aria-label={`${item.title}, ${item.percentComplete}% completado. Ver en Progreso`}
                  className="flex items-center gap-4 rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4 transition-all hover:border-xd-action hover:shadow-sm"
                >
                  <span className="flex items-center justify-center w-8 h-8 rounded-lg bg-xd-action/10">
                    {icon}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-[var(--color-text)] truncate">
                      {item.title}
                    </p>
                    <div className="xd-progress-track mt-1.5 h-1.5 overflow-hidden rounded-full">
                      <div
                        className="h-full rounded-full"
                        style={{
                          width: `${item.percentComplete}%`,
                          background: 'var(--xd-gradient-action)',
                        }}
                      />
                    </div>
                  </div>
                  <span className="text-xs text-[var(--color-text-muted)] whitespace-nowrap">
                    {item.percentComplete}%
                  </span>
                </Link>
              </ListItem>
            );
          })}
        </StaggerList>
      )}
    </section>
  );
}
